import type { DocumentOnCompleteCallbackImage } from '@innovatrics/dot-document-auto-capture';

import { Spacer } from '@dot/design-system';
import { Step } from '@repo/common';
import { useTranslation } from 'react-i18next';
import { default as styled } from 'styled-components';

import { onAutoCaptureError } from '../utils/error';

import { DocumentCamera } from './components/camera';
import { DocumentCameraUi } from './components/camera-ui';
import { DocumentInfoOverlay } from './components/info-overlay/info-overlay';
import { Instruction } from './components/instruction';
import { DocumentCaptureStep } from './constants';
import { useDocumentUpload } from './hooks/use-document-upload';

import { CaptureServerErrorOverlay } from '@/components/CaptureServerErrorOverlay/CaptureServerErrorOverlay';
import { DocumentRetakeOverlay } from '@/components/DocumentRetakeOverlay/DocumentRetakeOverlay';
import { ManualMessageUpload } from '@/components/ManualUpload/ManualMessageUpload';
import { SecuredByBadge } from '@/components/SecuredByBadge/SecuredByBadge';
import { CaptureWrapper } from '@/components/capture-wrapper';
import { config } from '@/config';
import { withStep } from '@/hoc/with-step';
import { useLockedMinHeight } from '@/hooks/use-locked-min-height';
import { setWorkflowAction } from '@/providers/app-provider/actions';
import { useAppContext } from '@/providers/app-provider/context';
import { postSubmitHandled } from '@/services/rivaas-api';
import * as GSC from '@/styles/GlobalStyles';
import { mapBffErrorCodeToFailReason } from '@/utils/bff-error-mapping';
import { applyBffSubmitResponse } from '@/utils/submit-error';
import { nextWorkflowFromSubmit } from '@/utils/step-workflow';
import { getPublicAsset } from '@/utils/utils';

const DOCUMENT_STEP_VALUES = [
  Step.DOCUMENT_FRONT,
  Step.DOCUMENT_BACK,
  Step.DOCUMENT_OCR_FRONT,
  Step.DOCUMENT_OCR_BACK,
  Step.AGE_DOCUMENT_FRONT,
] as const;

/**
 * Holds the captured height while the processing/review overlay covers a remounting
 * camera (see useLockedMinHeight) so the overlay — and its bottom-anchored Continue
 * button — doesn't collapse and jump while the next page's camera initializes.
 */
const SizeLockedContainer = styled(GSC.RelativeContainer)<{ $minHeight: null | number }>`
  ${(p) => (p.$minHeight === null ? '' : `min-height: ${p.$minHeight}px;`)}
`;

export const DocumentCapture = withStep({ only: [...DOCUMENT_STEP_VALUES] })(({ step }) => {
  const { t } = useTranslation();
  const { dispatch, state } = useAppContext();
  const {
    captureRenderKey,
    captureStep,
    continueToBackPage,
    dismissRetakeNotice,
    page,
    retakeNotice,
    retryCapture,
    serverErrorFailReason,
    serverErrorMessage,
    serverErrorRetryable,
    uploadDocument,
  } = useDocumentUpload(step);

  const handleSkipSecondPage = async () => {
    const data = await postSubmitHandled(undefined, { skipSteps: 1 });
    const redirectOptions = {
      unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
      verifiedUrl: state.customerData?.redirect?.verifiedUrl,
    };

    if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
      return;
    }

    const workflowBase = nextWorkflowFromSubmit(data, redirectOptions);

    if (workflowBase?.nextStep.value === Step.FAIL) {
      dispatch(
        setWorkflowAction({
          ...workflowBase,
          nextStep: {
            ...workflowBase.nextStep,
            failReason: mapBffErrorCodeToFailReason(data.error?.code),
          },
        }),
      );
    }
  };

  const handleComplete = async ({ image }: DocumentOnCompleteCallbackImage, content: Uint8Array): Promise<void> => {
    await uploadDocument(image, content);
  };

  const handleManualMessageUpload = (content: Uint8Array) => {
    handleComplete({ image: new Blob(['']), data: {} as DocumentOnCompleteCallbackImage['data'] }, content);
  };

  // The overlay's size comes from this container, and the container's height comes from
  // the camera — which remounts (key change) right when REVIEW appears. Lock the height
  // measured at PROCESSING (old camera still mounted) until the overlay is dismissed.
  const overlayShown =
    captureStep === DocumentCaptureStep.PROCESSING || captureStep === DocumentCaptureStep.REVIEW;
  const { minHeight, ref: containerRef } = useLockedMinHeight<HTMLDivElement>(overlayShown);

  return (
    <>
      <SizeLockedContainer $minHeight={minHeight} ref={containerRef}>
        <CaptureWrapper>
          <GSC.CaptureContainer>
            <Instruction captureStep={captureStep} />
            <DocumentCamera
              key={`${step.value}-${captureRenderKey}`}
              assetsDirectoryPath={getPublicAsset('/')}
              onComplete={handleComplete}
              onError={onAutoCaptureError}
            />
            <DocumentCameraUi
              appStateInstructions={{ loading: { text: t('common.state.loading') }, waiting: { visible: false } }}
              instructions={{
                candidate_selection: t('steps.documentCapture.instructions.candidateSelection'),
                document_centering: t('steps.documentCapture.instructions.documentCentering'),
                document_not_present: t('steps.documentCapture.instructions.documentNotPresent'),
                document_too_far: t('steps.documentCapture.instructions.tooFar'),
                sharpness_too_low: t('steps.documentCapture.instructions.sharpnessTooLow'),
                brightness_too_high: t('steps.documentCapture.instructions.brightnessTooHigh'),
                hotspots_present: t('steps.documentCapture.instructions.hotspotsPresent'),
              }}
            />
          </GSC.CaptureContainer>
        </CaptureWrapper>
        <DocumentInfoOverlay
          captureStep={captureStep}
          continueToSecondPage={continueToBackPage}
          image={page}
          skipSecondPage={handleSkipSecondPage}
        />

        {serverErrorFailReason && (
          <CaptureServerErrorOverlay
            failReason={serverErrorFailReason}
            message={serverErrorMessage}
            onRetry={retryCapture}
            retryable={serverErrorRetryable}
            unverifiedUrl={state.customerData?.redirect?.unverifiedUrl ?? ''}
          />
        )}

        {retakeNotice && (
          <DocumentRetakeOverlay onDismiss={dismissRetakeNotice} retake={retakeNotice} />
        )}
      </SizeLockedContainer>

      <Spacer $top={1}>
        <SecuredByBadge />
      </Spacer>

      {config.photoUploadEnabled && (
        <ManualMessageUpload id="document_manual_upload" onComplete={handleManualMessageUpload} />
      )}
    </>
  );
});
